import { memo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { SAFE } from '../BroadcastFrame.js';
import { PlayerBust, type BustPlayer, type BustState } from './PlayerBust.js';

/**
 * PlayerRail — the bottom lineup of PlayerBusts on the Stage.
 *
 * Each bust carries a framer-motion `layoutId` keyed on the player's id, so when
 * the roster changes (a death removes someone, a join adds someone) the remaining
 * busts slide to their new positions rather than snapping. Entrances stagger in.
 *
 * `highlight` spotlights one player (by id) and dims the rest — the reveal
 * choreography drives this. When null, everyone is 'normal'. Dead players are
 * always 'dead' regardless of highlight.
 *
 * Respects the BroadcastFrame SAFE margin (keeps busts clear of the title-safe
 * edge). Authored in the 1920×1080 design space (px units).
 */

export interface PlayerRailProps {
  players: BustPlayer[];
  /** Player id to spotlight; others dim. Null → all normal. */
  highlight?: string | null;
  /** Portrait diameter in design-space px. Default 120. */
  bustSize?: number;
}

function stateFor(player: BustPlayer, highlight: string | null | undefined): BustState {
  if (!player.alive) return 'dead';
  if (highlight == null) return 'normal';
  return player.id === highlight ? 'spotlit' : 'dim';
}

function PlayerRailImpl({ players, highlight = null, bustSize = 120 }: PlayerRailProps) {
  const reduced = useReducedMotion();

  return (
    <div
      style={{
        position: 'absolute',
        left: SAFE,
        right: SAFE,
        bottom: SAFE,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: Math.round(bustSize * 0.28),
        flexWrap: 'wrap',
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {players.map((player, i) => (
          <motion.div
            key={player.id}
            layout
            layoutId={player.id}
            initial={reduced ? false : { opacity: 0, y: 24, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
            transition={{
              duration: reduced ? 0 : 0.42,
              delay: reduced ? 0 : Math.min(i * 0.05, 0.4),
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <PlayerBust player={player} size={bustSize} state={stateFor(player, highlight)} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

export const PlayerRail = memo(PlayerRailImpl);
